import React, { useEffect, useMemo, useState } from "react";
import { promoterApi, toApiAbsoluteUrl } from "../../api/client";
import { useNavigate, Link } from "react-router-dom";
import { LinearProgress, Snackbar, Alert } from "@mui/material";
import { ChevronRight, FileText } from "lucide-react";

export default function PromoterProviders() {
  const [providers, setProviders] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const navigate = useNavigate();
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ q: "", page: 1, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const normalizedQuery = useMemo(
    () => (filters.q || "").trim().toLowerCase(),
    [filters.q]
  );

  const toAbsoluteUrl = toApiAbsoluteUrl;

  function formatBytes(bytes) {
    if (bytes === 0) return "0 B";
    if (!bytes || Number.isNaN(bytes)) return "-";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${
      sizes[i]
    }`;
  }

  function formatDate(iso) {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  }

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((p) => ({ ...p, q: searchText, page: 1 }));
    }, 200);
    return () => clearTimeout(handle);
  }, [searchText]);

  useEffect(() => {
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit]);

  async function runSearch(override = {}) {
    const merged = { ...filters, ...override };
    try {
      setLoading(true);
      const res = await promoterApi.listProviders({
        page: Number(merged.page || 1),
        limit: Number(merged.limit || 20),
        q: merged.q || "",
      });
      setProviders(res?.providers || []);
      setTotal(res?.pagination?.total || 0);
      setFilters((prev) => ({ ...prev, ...override }));
    } catch (_e) {
      setProviders([]);
      setTotal(0);
      const msg = _e?.data?.message || "Failed to load providers";
      setSnackbar({ open: true, message: msg });
    } finally {
      setLoading(false);
    }
  }

  const filteredProviders = useMemo(() => {
    if (!normalizedQuery) return providers;
    return (providers || []).filter((p) => {
      const email = (p?.email || "").toLowerCase();
      const phone = (p?.phoneNumber || "").toLowerCase();
      const company = (p?.companyName || "").toLowerCase();
      return (
        email.includes(normalizedQuery) ||
        phone.includes(normalizedQuery) ||
        company.includes(normalizedQuery)
      );
    });
  }, [providers, normalizedQuery]);

  return (
    <div>
      <div className="mb-4 text-sm text-gray-600 flex items-center gap-1">
        <Link to="/promoter/dashboard" className="hover:text-gray-800">
          Dashboard
        </Link>
        <ChevronRight className="h-4 w-4 opacity-60" />
        <span className="text-gray-800 font-medium">My Providers</span>
      </div>

      {/* Search bar */}
      <div className="bg-white shadow rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="Search company, email or phone"
          className="border p-2 rounded"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className="flex items-center gap-2">
          <button
            className="px-4 py-2 bg-gray-600 text-white rounded"
            onClick={() => {
              setSearchText("");
              setFilters({ q: "", page: 1, limit: filters.limit });
              runSearch({ page: 1 });
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Responsive table */}
      <div className="overflow-x-auto">
        <table className="w-full text-center bg-white shadow rounded min-w-[800px]">
          <thead>
            <tr>
              <th className="p-2 border">Company</th>
              <th className="p-2 border">Email</th>
              <th className="p-2 border">Phone</th>
              <th className="p-2 border">Payment Status</th>
              <th className="p-2 border">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredProviders.map((p) => {
              const company = p?.companyName || "-";
              const docs = p?.documents || [];
              return (
                <tr key={p.id}>
                  <td className="p-2 border">
                    {company !== "-" ? (
                      <button
                        className="text-blue-600 hover:text-blue-800 underline"
                        onClick={() =>
                          navigate(
                            `/promoter/listings?providerUserId=${encodeURIComponent(
                              p.id
                            )}`
                          )
                        }
                        title="View listings"
                      >
                        {company}
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="p-2 border">{p.email}</td>
                  <td className="p-2 border">{p.phoneNumber || "-"}</td>
                  <td className="p-2 border">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        p.paymentStatus === "verified"
                          ? "bg-green-100 text-green-800"
                          : p.paymentStatus === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {p.paymentStatus || "pending"}
                    </span>
                  </td>
                  <td className="p-2 border">
                    <button
                      onClick={() => setSelectedRecord(p)}
                      className="px-3 py-1 bg-blue-500 text-white rounded"
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
            {filteredProviders.length === 0 && (
              <tr>
                <td className="p-6 text-gray-500 text-center" colSpan={5}>
                  No providers
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between p-3 flex-wrap gap-2">
        <div className="text-sm text-gray-600">Total: {total}</div>
        <div className="flex items-center gap-2">
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            disabled={filters.page <= 1}
            onClick={() =>
              runSearch({ page: Math.max(1, (filters.page || 1) - 1) })
            }
          >
            Prev
          </button>
          <span className="text-sm">Page {filters.page}</span>
          <button
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            disabled={providers.length < filters.limit}
            onClick={() => runSearch({ page: (filters.page || 1) + 1 })}
          >
            Next
          </button>
          <select
            className="ml-2 border p-1 rounded text-sm"
            value={filters.limit}
            onChange={(e) =>
              runSearch({ limit: Number(e.target.value), page: 1 })
            }
          >
            <option value={10}>10 / page</option>
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        </div>
      </div>

      {/* Modal */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">User Details</h3>
            <p>Email: {selectedRecord.email}</p>
            <p>Role: {selectedRecord.role}</p>
            <p>Phone: {selectedRecord.phoneNumber || "-"}</p>
            <p className="mt-2 font-semibold">Provider Details</p>
            <p>Company: {selectedRecord.companyName || "-"}</p>
            <p>Phone: {selectedRecord.phoneNumber || "-"}</p>
            <p>Payment Status: {selectedRecord.paymentStatus || "-"}</p>
            <p>Payment Amount: ₹{selectedRecord.paymentAmount || "-"}</p>
            <p>Created: {formatDate(selectedRecord.createdAt)}</p>
            <button
              onClick={() => setSelectedRecord(null)}
              className="mt-4 px-4 py-2 bg-red-500 text-white rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Loader */}
      {loading && (
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <LinearProgress />
        </div>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: "" })}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
